import { useMemo } from 'react'

import { useFormContext } from 'react-hook-form'

import {
  numberFormatter,
  toDateTimeString,
  toLongDateTimeString,
} from '@fortem-monorepo/js-utilities'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@fortem-monorepo/react-ui'
import { Trades } from '@fortem-monorepo/web-sdk'

import { useCurrencies } from '@/react-query/queries/currencies'
import {
  useInstrumentTypesQuery,
  useInstrumentsByTypeQuery,
} from '@/react-query/queries/instruments'

const getBuyAndSellCurrencies = (options: { instrumentName?: string; quantity: number }) => {
  const { instrumentName, quantity } = options
  let firstCurrency = instrumentName?.split(' ')?.[1]?.substring(0, 3)
  let secondCurrency = instrumentName?.split(' ')?.[1]?.substring(3)

  return {
    buy: quantity >= 0 ? secondCurrency : firstCurrency,
    sell: quantity >= 0 ? firstCurrency : secondCurrency,
  }
}

interface BookTradeSummaryProps {}

export const BookTradeSummary: React.FC<BookTradeSummaryProps> = ({}) => {
  const form = useFormContext<Trades.BookTradeBody>()
  const trade = form.getValues()

  const instrumentTypeQuery = useInstrumentTypesQuery()
  const instrumentQuery = useInstrumentsByTypeQuery([trade.instrument_type_id])
  const currenciesQuery = useCurrencies()

  const instrumentTypeName = instrumentTypeQuery.data?.find(
    (t) => t.value === trade.instrument_type_id,
  )?.label

  const instrumentName = instrumentQuery.data?.find((i) => i.value === trade.instrument_id)?.label

  const currencyName = currenciesQuery.data?.find((c) => c.value === trade.currency_id)?.label

  const DateSummaries = useMemo(
    () => () => {
      return (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Trade Datetime</TableHead>
              <TableHead>Settlement Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>{toDateTimeString(new Date(trade.trade_datetime_utc))}</TableCell>
              <TableCell>{trade.settlement_date}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )
    },
    [trade.trade_datetime_utc, trade.settlement_date],
  )

  const price = trade.price_format === 'decimal' ? trade.price : trade.price / 100
  const priceFormatted = `${trade.price}${trade.price_format === 'percent' ? '%' : ''}`

  const { buy, sell } = getBuyAndSellCurrencies({
    instrumentName,
    quantity: trade.quantity,
  })

  const formatter = numberFormatter({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  return (
    <>
      <span>
        Confirm <span className={'font-semibold'}>{instrumentTypeName}</span> trade:
      </span>
      {trade.instrument_type_id === 5 ? (
        <>
          <p className={'text-sm'}>Ask Tech if you have any questions.</p>
          <p className={'text-sm'}>
            On{' '}
            <span className={'font-semibold'}>
              {toLongDateTimeString(new Date(trade.settlement_date))}
            </span>{' '}
            you will:
            <br /> - BUY {formatter({ value: Math.abs(trade.quantity) })} of {buy}
            <br /> - SELL {formatter({ value: Math.abs(trade.quantity) * price })} of {sell}
          </p>
        </>
      ) : (
        <>
          {/* FXFWD */}
          <p className={'text-sm bg-gray-50 rounded-md p-2'}>
            <span className={'font-semibold'}>
              {trade.quantity >= 0 ? 'BUY' : 'SELL'} {trade.quantity}{' '}
            </span>{' '}
            {trade.quantity === 1 ? 'unit' : 'units'} of{' '}
            <span className={'font-semibold'}>{instrumentName}</span> @{' '}
            <span className={'font-semibold'}>{priceFormatted}</span> for a total of{' '}
            <span className={'font-semibold'}>
              {currencyName} {formatter({ value: trade.notional })}
            </span>
          </p>
        </>
      )}
      {trade.instrument_type_id === 2 && trade.strategy_type !== 'BESPOKE' && (
        <p className="text-sm">
          Accrued Interests: {currencyName} {trade.accrued_interests}
        </p>
      )}
      <DateSummaries />
    </>
  )
}
